"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import InlineColorPicker from "@/app/(app)/editor/[id]/components/controlPanel/InlineColorPicker";
import styles from "./OnboardingSlideshow.module.css";

// Per the design doc, per-step saves go through this route (any subset of the
// known keys). The completion PATCH ({ onboardingCompleted: true }) uses it too.
const PREFERENCES_ENDPOINT = "/api/onboarding/preferences";

const DEFAULT_BRAND_COLOR = "#FF6702";

// Standard SaaS attribution options.
const REFERRAL_OPTIONS = [
  { key: "google", label: "Google / Search" },
  { key: "youtube", label: "YouTube" },
  { key: "twitter", label: "X / Twitter" },
  { key: "instagram_tiktok", label: "Instagram / TikTok" },
  { key: "friend", label: "Friend or colleague" },
  { key: "ai_tool", label: "AI tool (ChatGPT etc.)" },
  { key: "other", label: "Other" },
];

// Linear step order. After the color step the slideshow finishes and hands off
// to the guided tour, which ends with the "Upload a video" CTA.
const STEPS = ["intro", "attribution", "color"];
const TOTAL_PROGRESS = STEPS.length;

/**
 * Persist a subset of onboarding preferences. Best-effort: failures are logged
 * but never block the UI (each step is saved independently so a later retry on
 * a subsequent step still works).
 */
async function savePreferences(patch) {
  try {
    const response = await fetch(PREFERENCES_ENDPOINT, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      throw new Error(`PATCH ${PREFERENCES_ENDPOINT} -> ${response.status}`);
    }
    return true;
  } catch (error) {
    try {
      console.error("[OnboardingSlideshow] Failed to save preferences:", error);
    } catch {
      // ignore logging failures
    }
    return false;
  }
}

function ProgressBar({ stepIndex }) {
  return (
    <div className={styles.progress} aria-hidden="true">
      {Array.from({ length: TOTAL_PROGRESS }).map((_, i) => (
        <span
          key={i}
          className={`${styles.progressDot} ${
            i <= stepIndex ? styles.progressDotActive : ""
          }`}
        />
      ))}
    </div>
  );
}

export default function OnboardingSlideshow({
  onComplete,
  initialBrandColor,
  initialReferralSource,
  initialReferralSourceOther,
}) {
  const [step, setStep] = useState("intro");

  // Attribution state (saved per step on Next).
  const [referralSource, setReferralSource] = useState(
    initialReferralSource || "",
  );
  const [referralOther, setReferralOther] = useState(
    initialReferralSourceOther || "",
  );

  // Brand color state (saved per step on Next).
  const [brandColor, setBrandColor] = useState(
    initialBrandColor || DEFAULT_BRAND_COLOR,
  );

  const [isFinishing, setIsFinishing] = useState(false);
  const hasFinishedRef = useRef(false);

  const stepIndex = STEPS.indexOf(step);

  // ── Finish: persist completion, then hand control back to the gate. ──
  const finish = useCallback(async () => {
    if (hasFinishedRef.current) return;
    hasFinishedRef.current = true;
    setIsFinishing(true);
    await savePreferences({ onboardingCompleted: true });
    // Re-render the gate (which refetches the profile and starts the tour).
    onComplete?.();
  }, [onComplete]);

  // ── Navigation helpers ──────────────────────────────────────────────
  const goNext = useCallback(async () => {
    if (step === "intro") {
      setStep("attribution");
      return;
    }
    if (step === "attribution") {
      // Save attribution before advancing.
      await savePreferences({
        referralSource: referralSource || null,
        referralSourceOther: referralSource === "other" ? referralOther : null,
      });
      setStep("color");
      return;
    }
    if (step === "color") {
      // Final step: save the brand color, then finish and hand off to the tour.
      await savePreferences({ defaultPlayerColor: brandColor });
      void finish();
      return;
    }
  }, [step, referralSource, referralOther, brandColor, finish]);

  const goBack = useCallback(() => {
    if (step === "attribution") setStep("intro");
    else if (step === "color") setStep("attribution");
  }, [step]);

  const handleColorChange = useCallback((hex) => {
    setBrandColor(hex);
  }, []);

  // ── Render per step ─────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case "intro":
        return (
          <div className={styles.stepBody}>
            <div className={styles.stepHeader}>
              <Image
                src="/icons/FlareIcon.png"
                alt="Flare"
                width={44}
                height={44}
                className={styles.logo}
                priority
              />
              <h2 className={styles.title}>Welcome to Flare</h2>
              <p className={styles.subtitle}>
                Take 30 seconds to see what you can build.
              </p>
            </div>
          </div>
        );

      case "attribution":
        return (
          <div className={styles.stepBody}>
            <div className={styles.stepHeader}>
              <h2 className={styles.title}>Where did you hear about Flare?</h2>
              <p className={styles.subtitle}>
                This helps us reach more people like you.
              </p>
            </div>
            <div className={styles.optionsGrid}>
              {REFERRAL_OPTIONS.map((option) => {
                const selected = referralSource === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`${styles.optionButton} ${
                      selected ? styles.optionButtonSelected : ""
                    }`}
                    onClick={() => setReferralSource(option.key)}
                  >
                    <span
                      className={`${styles.optionRadio} ${
                        selected ? styles.optionRadioSelected : ""
                      }`}
                    />
                    {option.label}
                  </button>
                );
              })}
              {referralSource === "other" && (
                <input
                  type="text"
                  className={styles.otherInput}
                  placeholder="Tell us where…"
                  value={referralOther}
                  onChange={(e) => setReferralOther(e.target.value)}
                  autoFocus
                />
              )}
            </div>
          </div>
        );

      case "color":
        return (
          <div className={styles.stepBody}>
            <div className={styles.stepHeader}>
              <h2 className={styles.title}>Pick your brand color</h2>
              <p className={styles.subtitle}>
                New videos use this as their player color. You can change it
                later in settings.
              </p>
            </div>
            <div className={styles.colorLayout}>
              <div className={styles.colorPickerCol}>
                <InlineColorPicker
                  color={brandColor}
                  onChange={handleColorChange}
                  showAlpha={false}
                />
              </div>
              <div className={styles.previewCol}>
                <span className={styles.previewLabel}>Preview</span>
                <div className={styles.playerChip}>
                  <div className={styles.playerChipScreen}>
                    <div
                      className={styles.playButton}
                      style={{ backgroundColor: brandColor }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div
                    className={styles.playerChipBar}
                    style={{ backgroundColor: brandColor }}
                  />
                </div>
                <div className={styles.swatchRow}>
                  <span
                    className={styles.swatch}
                    style={{ backgroundColor: brandColor }}
                  />
                  {brandColor}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Footer (Back / Next / Finish) ───────────────────────────────────
  const renderFooter = () => {
    const showBack = step !== "intro";

    // The color step is the last step, so it finishes; earlier steps advance.
    const isLastStep = step === "color";
    const nextDisabled =
      step === "attribution" &&
      (!referralSource ||
        (referralSource === "other" && referralOther.trim() === ""));
    const primary = (
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => void goNext()}
        disabled={nextDisabled || (isLastStep && isFinishing)}
      >
        {isLastStep ? (isFinishing ? "Finishing…" : "Finish") : "Next"}
      </button>
    );

    return (
      <div className={styles.footer}>
        {showBack ? (
          <button type="button" className={styles.backBtn} onClick={goBack}>
            Back
          </button>
        ) : (
          <span />
        )}
        <div className={styles.footerSpacer} />
        {primary}
      </div>
    );
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Flare onboarding"
    >
      <div className={styles.card}>
        <ProgressBar stepIndex={stepIndex} />
        {renderStep()}
        {renderFooter()}
      </div>
    </div>
  );
}
